import React, {
  createContext, useContext, useCallback, useEffect, useRef, useState,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  getAdditionalUserInfo,
  linkWithCredential,
  fetchSignInMethodsForEmail,
} from 'firebase/auth';
import { clearIndexedDbPersistence } from 'firebase/firestore';
import { auth, db, isConfigured } from '../lib/firebase';
import { ANALYTICS, trackEvent } from '../lib/analytics';
import { noteSignUp } from '../lib/adConversions';
import { metaServerSignals } from '../lib/metaPixel';
import { writeQueue } from '../data/writes';
import { theReadPath } from '../data/readStore';
import { forgetServerAnswers } from '../data/serverAnswers';
import { deleteOwnAccount } from '../data/accountDeletion';
import { googleProvider } from '../lib/googleAuth';
import { googleNeedsRedirect } from '../domain/authErrors';
import { reauthMethod as reauthMethodFor } from '../domain/signIn';
import { claimsOfIdToken, hasPortalClaim } from '../domain/identity';

const AuthContext = createContext(null);

/**
 * Everything one teacher's session leaves on this device, ended (#282, #283,
 * #207 U7). The queue, the outstanding-write ledger and the read store cannot
 * know when a session ends; this module can.
 *
 * A write still owed is parked for that teacher's return: not stranded, and
 * sign-out is not refused until it settles (offline that would mean never).
 * `queue.endSession()` releases this session's hold — in-flight records go
 * back to `pending`, and a commit still with the SDK can no longer settle
 * anything — and leaves every batch durable. `writes.js`'s dispatch guard then
 * holds a batch whose studio is not the signed-in one until that teacher signs
 * back in, and `queue.wake()` in `SyncContext` sends it then.
 *
 * `queue.reset()` is not called: it clears the store, which on a shared laptop
 * would delete the previous teacher's unsent work.
 *
 * The read store is cleared (U7): it is a cache of that studio, and leaving it
 * would let the next teacher read the previous one's students offline.
 * `clearStudio(uid)` is a key-range delete over `users/{uid}/`, which is why
 * `appStore.js` keys on the full path. It is cleared whatever `readStoreFlag`
 * says, since rows stored under an earlier flag setting must still go.
 *
 * The outstanding-write ledger is not cleared: those notes are the durable half
 * of #167 for writes the queue still owes. The chip scopes its count instead
 * (`outstandingWritesFor(uid)`).
 */
async function endSession(uid) {
  // Both are attempted whatever the other does: a queue whose IndexedDB will
  // not open must not leave the previous teacher's students on the screen, and
  // a store that will not clear must not leave the queue holding its session.
  const results = await Promise.allSettled([
    writeQueue().endSession(),
    theReadPath().store.clearStudio(uid),
  ]);
  const failures = results.filter((r) => r.status === 'rejected');
  if (failures.length) {
    // The sign-out itself went through — Firebase's `signOut` is not in this
    // promise — so the message says what this device still carries.
    console.error(
      `[auth] signing out of \`${uid}\` left ${failures.length} of 2 local clean-ups undone; `
      + 'this device may still be holding that studio\'s stored reads or its write session:',
      failures.map((f) => f.reason),
    );
  }
}

/**
/**
 * How long a sign-in will wait for the SDK's cache to go before giving up.
 *
 * `clearIndexedDbPersistence` deletes an IndexedDB database, and
 * `deleteDatabase` blocks while another connection is open — such as another
 * tab of this app, which `persistentMultipleTabManager` supports. Without a
 * budget that tab would hold the next teacher on a loading screen.
 */
export const SWEEP_BUDGET_MS = 3000;

/** Where this browser records whose documents the SDK's cache was filled with. */
const SDK_CACHE_OWNER_KEY = 'coda-sdk-cache-owner';

/**
 * The limit this sweep still has, in the message that reports it. Exported so a
 * test can assert it is stated. A clear that has not finished inside the
 * budget may finish a moment later or be blocked by another tab for good, and
 * the mechanism cannot tell those apart, so it gives both.
 */
export const SDK_CACHE_SWEEP_UNCONFIRMED =
  `the Firestore SDK's own cache did not confirm it cleared within ${SWEEP_BUDGET_MS} ms. `
  + 'It may finish a moment from now, or it may be blocked by another tab of this app holding '
  + 'the database open, in which case it will not finish at all. This cannot tell those apart, '
  + 'so those documents may still be on this device (#302)';

function sdkCacheOwner() {
  try { return globalThis.localStorage?.getItem(SDK_CACHE_OWNER_KEY) ?? null; } catch { return null; }
}

function rememberSdkCacheOwner(uid) {
  try { globalThis.localStorage?.setItem(SDK_CACHE_OWNER_KEY, uid); } catch { /* private mode */ }
}

/**
 * Does signing in as `uid` have to drop the SDK's cache before anything reads?
 *
 * Synchronous, because the auth observer decides whether to hold `setUser`
 * back before doing anything asynchronous, and `localStorage` is the only
 * store that can answer synchronously.
 *
 * A missing record counts as somebody else's: it cannot tell a first sign-in
 * here (empty cache, free clear) from a record wiped while the cache survived,
 * and the cost of being wrong is re-fetching a cache.
 */
export function sdkCacheNeedsSweep(uid) {
  // `indexedDB` is the cache. Without it the SDK is in memory and there is
  // nothing on this device to clear — the ordinary state under jsdom.
  if (!globalThis.indexedDB) return false;
  return !!uid && !!db && sdkCacheOwner() !== uid;
}

async function sweepSdkCache(uid) {
  if (!sdkCacheNeedsSweep(uid)) return;
  // What the cache vouched for goes with it (#651): a mark that the server had
  // answered a list here, outliving the documents, would make the emptied
  // cache read as a confirmed empty studio. Done before the attempt, so a
  // clear that hangs or refuses leaves no mark (src/data/serverAnswers.js).
  forgetServerAnswers();
  let settled = false;
  try {
    await Promise.race([
      clearIndexedDbPersistence(db).then(() => { settled = true; }),
      new Promise((resolve) => { setTimeout(resolve, SWEEP_BUDGET_MS); }),
    ]);
  } catch (err) {
    // The clear refused rather than hung — a started client, most likely. The
    // record is not updated, so the next sign-in tries again.
    //
    // A warning, not an error (#459): a first sign-in on a device counts as
    // somebody else's cache (`sdkCacheNeedsSweep`), so this fires on the
    // ordinary path.
    console.warn(
      `[auth] signing in as \`${uid}\` could not clear the Firestore SDK's own cache, so the `
      + 'previous studio\'s documents are still on this device (#302):', err,
    );
    return;
  }
  if (!settled) {
    console.warn(`[auth] ${SDK_CACHE_SWEEP_UNCONFIRMED}.`);
    return;
  }
  rememberSdkCacheOwner(uid);
}

/**
 * A session claiming this device (#302).
 *
 * `endSession` runs only for ways a session ends that run code. A closed tab
 * runs none, so the stored reads of a teacher who shut the laptop stay in
 * `coda-app-state` for whoever signs in next. Every read is keyed by the
 * signed-in uid, so this is data at rest, never wrong data on screen.
 *
 * Only the read store and the SDK's cache are deleted. The write queue and
 * the outstanding-write ledger are scoped instead (#282, #283), because they
 * hold work still owed; a cache holds nothing owed.
 *
 * The SDK keeps its own IndexedDB cache (`persistentLocalCache` in
 * `src/lib/firebase.js`), keyed per app rather than per uid, and
 * `clearIndexedDbPersistence` needs a client that has not started. Nothing in
 * this app touches Firestore before `setUser` — every provider is gated on
 * `user`, and `SyncContext` gates `startWriteQueue` on `loading` — so the auth
 * observer holds both back until this resolves, and the clear runs before the
 * client starts. No `terminate()`, and the module singleton is the same object
 * afterwards. A transition that has to sweep stays in its loading state for up
 * to `SWEEP_BUDGET_MS`; the same teacher signing back in awaits nothing.
 *
 * The SDK's cache includes its mutation queue, so this drops unacknowledged
 * writes the previous teacher made. They are not lost: `src/data/queue.js` is
 * their replayer, its records live in `coda-write-queue`, which this does not
 * touch, and #282 scopes them so they go out when that teacher signs back in.
 *
 * Neither half may fail a sign-in; failures are logged.
 */
async function claimDevice(uid) {
  try {
    const cleared = await theReadPath().store.clearOtherStudios(uid);
    if (cleared.length) {
      console.warn(
        `[auth] cleared stored reads for ${cleared.length} studio(s) this device was still `
        + `holding after a session that never signed out (#302): ${cleared.join(', ')}.`,
      );
    }
  } catch (err) {
    console.error(
      `[auth] signing in as \`${uid}\` could not sweep other studios' stored reads from this `
      + 'device, so it may still be carrying them:', err,
    );
  }
  // After the store above rather than beside it; each owns its own try, so a
  // failure in either does not skip the other.
  await sweepSdkCache(uid);
}

/** Ended sessions run one at a time, so two fast transitions cannot interleave. */
let ending = Promise.resolve();

// ── Signing in with Google (#567) ──────────────────────────────────────────
//
// A pop-up first, and a full-page redirect only when the pop-up cannot open
// (`googleNeedsRedirect`). The redirect flow depends on storage that Safari and
// Chrome partition between the app's domain and Firebase's auth handler, so
// it is kept for browsers that refuse the pop-up.
//
// A redirect's outcome arrives on the next page load through
// `getRedirectResult`, which is asked only when this tab started one: the flag
// below is set just before leaving and cleared on the way back, so an
// ordinary start-up makes no extra round trip.
const GOOGLE_REDIRECT_KEY = 'coda.googleRedirect';

function rememberGoogleRedirect() {
  try { globalThis.sessionStorage?.setItem(GOOGLE_REDIRECT_KEY, '1'); } catch { /* private mode */ }
}

function takeGoogleRedirect() {
  try {
    const was = globalThis.sessionStorage?.getItem(GOOGLE_REDIRECT_KEY) === '1';
    globalThis.sessionStorage?.removeItem(GOOGLE_REDIRECT_KEY);
    return was;
  } catch {
    return false;
  }
}

/**
 * Keep the Google credential an `account-exists-with-different-credential`
 * refusal carries, so that it can be linked once the teacher has signed in
 * with their password. Returned on the error, which is the only thing the
 * form gets back.
 */
function withPendingGoogle(err) {
  if (err?.code === 'auth/account-exists-with-different-credential') {
    try {
      err.pendingCredential = GoogleAuthProvider.credentialFromError(err) || null;
    } catch {
      err.pendingCredential = null;
    }
  }
  return err;
}

/**
 * What every new account gets, whichever door made it: the sign-up counted,
 * the way to /welcome opened (#754, src/hooks/useThankYouPages.js), and the
 * studio's first two documents written (data/newStudio.js).
 *
 * Imported when needed rather than at the top: it reaches the settings and
 * onboarding providers, which import this file.
 *
 * Not for a family's portal login (#764), which is not a new studio. Only the
 * server makes those logins, so this check reads the claim off the token the
 * credential already holds, with no network.
 */
async function welcomeNewAccount(uid, method, user = null) {
  if (user && hasPortalClaim(claimsOfIdToken(user.accessToken))) return;
  // The uid and email go on only as the Meta Pixel's hashes (#780).
  noteSignUp({ uid, email: user?.email ?? null });
  trackEvent(ANALYTICS.signUp, { method });
  // The sign-up's call to the server, with the cookie notice's answer in
  // force (#781, functions/billing/callables.js `ensureAccount`). Not awaited,
  // and it never rejects.
  try {
    const signals = metaServerSignals();
    import('../data/useEntitlement').then(
      ({ ensureAccountAtSignUp }) => ensureAccountAtSignUp(signals),
      () => null,
    );
  } catch {
    // Nothing here is the sign-up's business.
  }
  try {
    const { seedNewStudio } = await import('../data/newStudio');
    await seedNewStudio(uid);
  } catch (err) {
    console.error('[sign-up] could not seed the new studio:', err);
  }
}

/** After any Google sign-in, by pop-up or by redirect. */
async function afterGoogleSignIn(cred) {
  if (cred?.user && getAdditionalUserInfo(cred)?.isNewUser) {
    await welcomeNewAccount(cred.user.uid, 'google', cred.user);
  }
  return cred;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Held beside `user` rather than read off it: the flag flips on Firebase's
  // servers when the mail's link is clicked, and `reload()` mutates the same
  // user object in place, so nothing about the reference changes.
  const [emailVerified, setEmailVerified] = useState(false);
  // Who this tab was signed in as last time Firebase said anything, so that a
  // change can be noticed. Null means no previous session on this tab to end.
  const signedInAs = useRef(null);
  // A Google redirect that came back refused (#567). A redirect's refusal
  // arrives on a fresh page load before any form exists, so it waits here for
  // the sign-in page to show it.
  const [redirectError, setRedirectError] = useState(null);

  // Finish a Google redirect this tab started (see `takeGoogleRedirect`). The
  // user arrives through the auth observer below; this handles what only the
  // result can say: whether the account is new, and why it was refused.
  useEffect(() => {
    if (!isConfigured || !takeGoogleRedirect()) return;
    getRedirectResult(auth).then(afterGoogleSignIn, (err) => {
      setRedirectError(withPendingGoogle(err));
    });
  }, []);

  useEffect(() => {
    if (!isConfigured) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      // A session ends here rather than inside `logout()`: a revoked refresh
      // token, a password changed on another device and a sign-out in another
      // tab all arrive here and never touch `logout`.
      //
      // A closed tab runs nothing, which is why `SyncContext` and
      // `outstandingWrites.js` also scope the queue and ledger by uid, and
      // `claimDevice` sweeps stored reads when the next session claims the
      // device (#302).
      //
      // Started before `setUser` so the queue's generation has moved before any
      // effect keyed on the new uid can start a dispatch, and chained so two
      // fast transitions cannot interleave. Not awaited: the sign-out has
      // already happened.
      const next = firebaseUser?.uid ?? null;
      const previous = signedInAs.current;
      signedInAs.current = next;
      if (previous && previous !== next) {
        // Two-argument `then`: `endSession` reports its own failures and
        // should not reject, so anything arriving here is a bug in it.
        ending = ending.then(() => endSession(previous)).then(undefined, (err) => {
          console.error('[auth] ending the previous session threw:', err);
        });
      }

      // A session claiming the device (#302), on the same chain as the end
      // above: if they raced, B's sweep could land before A's `clearStudio`
      // and leave A's rows behind.
      //
      // `next !== previous` because claiming happens once per session;
      // `onAuthStateChanged` can fire again for the same uid.
      //
      // Awaited only when the SDK's cache has to go: publishing `user` or
      // `loading` is what starts the client (see `claimDevice`), so such a
      // transition publishes both after the sweep. The decision is synchronous,
      // off `localStorage`, because a promise would answer after the window had
      // closed.
      const publish = () => {
        setUser(firebaseUser);
        setEmailVerified(firebaseUser?.emailVerified === true);
        setLoading(false);
      };

      if (next && next !== previous) {
        const claimed = ending.then(() => claimDevice(next));
        ending = claimed;
        if (sdkCacheNeedsSweep(next)) {
          // Two-argument `then`: `claimDevice` reports its own failures and
          // should not reject. The session is published either way, so a
          // clean-up that threw does not leave a teacher on a loading screen.
          claimed.then(publish, (err) => {
            console.error('[auth] claiming the device threw:', err);
            publish();
          });
          return;
        }
      }

      publish();
    });

    return unsubscribe;
  }, []);

  // `pendingGoogle` is the Google credential from an earlier
  // `account-exists-with-different-credential` refusal (#567). The password
  // proves the account is theirs, so Google is linked to it. A failed link is
  // logged and does not undo the sign-in.
  async function login(email, password, pendingGoogle = null) {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    if (pendingGoogle && cred?.user) {
      try {
        await linkWithCredential(cred.user, pendingGoogle);
        // Google vouches for the address, so linking may confirm an address
        // the password account never did; the flag is re-read for the gate.
        await cred.user.reload();
        setEmailVerified(cred.user.emailVerified === true);
      } catch (err) {
        console.warn('[auth] signed in, but could not link the Google account:', err);
      }
    }
    return cred;
  }

  // Which ways an address signs in, asked only after a password was refused,
  // so the form can tell an account Google took over from a mistyped
  // password (#591, domain/authErrors.js `classifyPasswordSignIn`). Null when
  // Firebase could not say. With email enumeration protection on it answers
  // an empty list for every address, and the form keeps its generic message.
  async function signInMethodsFor(email) {
    try {
      return await fetchSignInMethodsForEmail(auth, email);
    } catch {
      return null;
    }
  }

  // Sign in, or sign up, with Google (#567). One button does both, as
  // Firebase does: an address it has not seen is a new account.
  //
  // Resolves with the credential, or with null when the page is about to
  // leave for Google's redirect, in which case the outcome arrives on the
  // next load (`redirectError`, and the auth observer). Rejects with
  // Firebase's error otherwise, carrying `pendingCredential` when the address
  // already has a password account.
  //
  // A Google account is not shown the check-your-email screen, and not
  // because of its provider: Google vouches for the address, so Firebase
  // records it with `emailVerified: true`, and that flag is all the gate reads
  // (domain/signIn.js).
  async function signInWithGoogle() {
    let cred;
    try {
      cred = await signInWithPopup(auth, googleProvider());
    } catch (err) {
      if (googleNeedsRedirect(err)) {
        rememberGoogleRedirect();
        await signInWithRedirect(auth, googleProvider());
        return null;
      }
      throw withPendingGoogle(err);
    }
    return afterGoogleSignIn(cred);
  }

  // Sign up, and ask Firebase to send the confirmation mail.
  //
  // A failed send does not fail the sign-up: the account already exists and
  // could not be created again with the same address. It is logged, and the
  // check-your-email screen's Resend button recovers. A trial studio may not
  // send receipts or reminders until its address is confirmed
  // (functions/billing/entitled.js `senderMayDeliver`).
  //
  // Firebase sends this itself, with no mail transport of ours; against the
  // auth emulator the link is written to the emulator log.
  //
  // An email/password account is kept out of the app until its address is
  // confirmed (#566): the auth observer publishes it unverified, and
  // `SignInGate` in App.jsx draws the check-your-email screen. The studio's
  // first documents are still written now; nothing reads them until the gate
  // opens.
  async function register(email, password) {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    try {
      await sendEmailVerification(cred.user);
    } catch (err) {
      console.warn('Could not send the verification email', err);
    }
    // Counted once the account exists (#458), and seeded the way a Google
    // sign-up is.
    await welcomeNewAccount(cred.user.uid, 'password', cred.user);
    return cred;
  }

  async function resendVerification() {
    if (!auth.currentUser) throw new Error('Sign in first.');
    return sendEmailVerification(auth.currentUser);
  }

  // Ask Firebase again whether the link has been clicked: the check-your-email
  // screen's "I've verified" (#566). There is no push for this, and polling
  // would cost a call a minute per signed-in studio.
  //
  // A true answer also refreshes the ID token: `reload()` updates the user but
  // not the token, whose `email_verified` claim would say `false` for up to an
  // hour. No server code reads that claim (the device-link callables read Auth
  // fresh), so a refresh that fails does not hold the teacher back.
  const refreshVerification = useCallback(async () => {
    if (!auth.currentUser) return false;
    await auth.currentUser.reload();
    const verified = auth.currentUser.emailVerified === true;
    if (verified) await auth.currentUser.getIdToken(true).then(undefined, () => null);
    setEmailVerified(verified);
    return verified;
  }, []);

  // A bare `signOut`: what this device has to forget is done by the auth
  // observer above, which sees every way a session ends. See `endSession`.
  async function logout() {
    return signOut(auth);
  }

  // A family's first sign-in, with the token the Family Portal's redeem
  // returned (#770, functions/portal/invites.js). Not a sign-up: the server
  // made the login with the portal claim, so nothing here welcomes or counts it
  // (#764). StudioGate sends it to /portal.
  async function signInWithPortalToken(token) {
    return signInWithCustomToken(auth, token);
  }

  // Firebase sends this itself; against the auth emulator the reset link is
  // written to the emulator log.
  async function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // Delete this teacher's account and studio (#432): re-authenticate, ask the
  // `deleteAccount` callable, then forget the studio on this device and sign
  // out (src/data/accountDeletion.js). Rejects with a `reason` the core has a
  // sentence for, and only ever with the account intact. `reauthMethod` says
  // whether to prove identity with the password or with Google (#567).
  async function deleteAccount({ password, confirmation }) {
    return deleteOwnAccount({ password, confirmation, method: reauthMethod });
  }

  // Read off `providerData` on every render rather than held in state: a link
  // (`login` above) changes it on the same user object, and this is cheap.
  const reauthMethod = reauthMethodFor((user?.providerData || []).map((p) => p?.providerId));

  const clearRedirectError = useCallback(() => setRedirectError(null), []);

  const value = {
    user,
    loading,
    emailVerified,
    login,
    register,
    signInWithGoogle,
    signInMethodsFor,
    redirectError,
    clearRedirectError,
    reauthMethod,
    logout,
    resetPassword,
    signInWithPortalToken,
    deleteAccount,
    resendVerification,
    refreshVerification,
    isConfigured,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
